/**
 * Walkthrough script (PROD-595).
 *
 * Copy-only data file. Each step carries:
 *   - `id` - stable identifier used in telemetry payloads + as the
 *     `data-walkthrough-anchor` value on the anchor target. Closed enum
 *     so a typo never silently widens the script.
 *   - `anchorSelector` - CSS selector of the live UI surface the
 *     coachmark anchors to. Resolved at render time via
 *     `document.querySelector` + `getBoundingClientRect()`.
 *   - `title` - verbatim title; HC5 hyphens, never em dashes.
 *   - `body` - 1 to 3 sentence body copy. The first step interpolates
 *     `{tenant}` at render time from `useTenantStore`.
 *   - `nextLabel` / `prevLabel` / `skipLabel` - canonical button copy.
 *
 * The 8 steps below match the PROD-595 ticket spec verbatim. PMs edit
 * this file; React renders it. No interpolation or computation lives
 * here other than the `{tenant}` token in step 1.
 *
 * HC5: zero em / en dashes anywhere in this file.
 */

export type WalkthroughStepId =
  | 'welcome'
  | 'synthetic-patients'
  | 'tenant-switch'
  | 'mode-toggle'
  | 'nba-cards'
  | 'override-flow'
  | 'learning-loop'
  | 'pathway-editor';

export interface WalkthroughStep {
  /** Stable id - load-bearing in telemetry + the anchor data attribute. */
  id: WalkthroughStepId;
  /** CSS selector for the anchor target in the live UI. */
  anchorSelector: string;
  /** Verbatim coachmark title. HC5 hyphens, no em dashes. */
  title: string;
  /** Verbatim coachmark body copy. May reference `{tenant}` (step 1 only). */
  body: string;
}

/**
 * Token interpolated into step 1's body. Resolved at render time from
 * `useTenantStore.getState().tenantId` mapped through the tenant-config
 * registry's `displayName`.
 */
export const TENANT_TOKEN = '{tenant}';

/** Verbatim button labels - HC5 hyphens, never em dashes. */
export const WALKTHROUGH_NEXT_LABEL = 'Next';
export const WALKTHROUGH_PREV_LABEL = 'Previous';
export const WALKTHROUGH_SKIP_LABEL = 'Skip';
export const WALKTHROUGH_FINISH_LABEL = 'Finish';
export const WALKTHROUGH_RELAUNCH_LABEL = 'Show walkthrough';

/**
 * The canonical 8-step demo walkthrough script. Verbatim copy from the
 * PROD-595 ticket; do not paraphrase. Ordering is load-bearing.
 */
export const WALKTHROUGH_STEPS: ReadonlyArray<WalkthroughStep> = [
  {
    id: 'welcome',
    anchorSelector: '[data-walkthrough-anchor="welcome"]',
    title: 'Welcome',
    body: `This is Care Pathway Studio for ${TENANT_TOKEN}.`,
  },
  {
    id: 'synthetic-patients',
    anchorSelector: '[data-walkthrough-anchor="synthetic-patients"]',
    title: 'Patients are synthetic',
    body: 'Watch for the [SYNTHETIC] badge. Every patient on this surface is a fixture; no real PHI ever lands here.',
  },
  {
    id: 'tenant-switch',
    anchorSelector: '[data-walkthrough-anchor="tenant-switch"]',
    title: 'Switch tenants',
    body: 'Switch tenants to see how the same engine adapts. Apex Cardiometabolic and Longevity Health Institute share the same code path; the pathways, source library, and NBA candidates re-tenant on click.',
  },
  {
    id: 'mode-toggle',
    anchorSelector: '[data-walkthrough-anchor="mode-toggle"]',
    title: 'Demo or Live mode',
    body: 'Toggle Demo / Live to see deterministic demo vs real LLM responses. Demo Mode is fully scripted for safe walkthroughs; Live Mode hits the routed LLM with full guardrails.',
  },
  {
    id: 'nba-cards',
    anchorSelector: '[data-walkthrough-anchor="nba-cards"]',
    title: 'NBA cards cite their sources',
    body: 'Next-Best-Action cards cite corpus chunks and offer one-click Override. Every card surfaces its evidence so the clinician can audit the suggestion in seconds.',
  },
  {
    id: 'override-flow',
    anchorSelector: '[data-walkthrough-anchor="override-flow"]',
    title: 'Override flow',
    body: 'The override flow captures structured reason. Watch the confidence drop, not vanish. The system records the dissent without suppressing future suggestions.',
  },
  {
    id: 'learning-loop',
    anchorSelector: '[data-walkthrough-anchor="learning-loop"]',
    title: 'Learning Loop',
    body: 'Learning Loop shows the system metabolizing every override. Every dissent reweights the model, surfaces metrics, and feeds the next iteration of the pathway.',
  },
  {
    id: 'pathway-editor',
    anchorSelector: '[data-walkthrough-anchor="pathway-editor"]',
    title: 'Pathway Editor',
    body: 'Pathway Editor lets you author the next iteration. Drag, edit, and publish protocol changes that the engine can route against on the very next encounter.',
  },
];

/** Total step count, exposed so the store + tests do not recount. */
export const WALKTHROUGH_TOTAL_STEPS = WALKTHROUGH_STEPS.length;

/**
 * Resolve a step by index. Out-of-range index returns null so callers
 * can defensively no-op rather than crash.
 */
export function getWalkthroughStep(index: number): WalkthroughStep | null {
  if (index < 0 || index >= WALKTHROUGH_STEPS.length) return null;
  return WALKTHROUGH_STEPS[index];
}

/**
 * Interpolate the `{tenant}` token in a step's body. Pure helper; the
 * caller passes the resolved display name. If the body does not contain
 * the token, returns the body unchanged.
 */
export function interpolateTenant(body: string, tenantDisplayName: string): string {
  return body.split(TENANT_TOKEN).join(tenantDisplayName);
}
